// ─── Structured-data (JSON-LD) helpers for SEO ───
// All schemas use https://schema.org vocabulary.
// Reference docs: https://developers.google.com/search/docs/appearance/structured-data

const ORG_ID = "https://ireberlin.com/#organization";
const SITE_URL = "https://ireberlin.com";
const LOGO_URL = "https://ireberlin.com/ire-logo-1024.png";

// ─── Anonymised client testimonials (used for Review JSON-LD) ───
// Single source of truth — also referenced by the on-page testimonials carousel.
export const clientReviews = [
  {
    author: "M.K., Berlin",
    context: "2024 acquisition · Tech executive, family",
    rating: 5,
    headline: "Turned my March tax bill into next year's rent income.",
    body: "I had no idea Sonder-AfA could be combined with the new energy-efficiency rules. The strategy call alone was worth more than two years of my old advisor's retainer.",
    datePublished: "2024-09-12",
  },
  {
    author: "A.R., Munich → Berlin",
    context: "2024 acquisition · Senior consultant, expat",
    rating: 5,
    headline: "The first advisor who could explain it in English without dumbing it down.",
    body: "IRE walked me through the full structure: financing, tax, rental projection, exit. Everything documented. They negotiated 0.4% off the bank's first quote.",
    datePublished: "2024-11-04",
  },
  {
    author: "T.S., Frankfurt",
    context: "2023 acquisition · MD, finance · €18k/yr tax saving",
    rating: 5,
    headline: "Disciplined, conservative, and zero brokerage fees on my side.",
    body: "I'd been burnt before by 'tax-shelter' property pitches. IRE's underwriting was the opposite — every number stress-tested, every assumption labelled. Two years in, the model has tracked reality within 2%.",
    datePublished: "2023-12-19",
  },
  {
    author: "J.W., Berlin",
    context: "2024 acquisition · MedTech founder",
    rating: 5,
    headline: "From first call to keys in 9 weeks — entirely while I was scaling my company.",
    body: "I needed something I didn't have to manage. They handled the notary, the bank, the property manager, even the Steuerberater hand-off. I just signed.",
    datePublished: "2024-06-28",
  },
  {
    author: "N.D., Hamburg",
    context: "2024 acquisition · Lawyer, EU citizen",
    rating: 5,
    headline: "They will tell you when you shouldn't buy. That's why I trust them.",
    body: "On my first call they suggested I wait six months until my rental income from another property completed. Most advisors would have closed me. They didn't. I came back when ready and bought.",
    datePublished: "2024-08-15",
  },
];

const reviewNodes = (reviews) =>
  reviews.map((r) => ({
    "@type": "Review",
    author: { "@type": "Person", name: r.author },
    reviewRating: {
      "@type": "Rating",
      ratingValue: r.rating,
      bestRating: 5,
      worstRating: 1,
    },
    name: r.headline,
    reviewBody: r.body,
    datePublished: r.datePublished,
    itemReviewed: { "@id": ORG_ID },
  }));

const aggregateRatingNode = (reviews) => {
  if (!reviews.length) return undefined;
  const sum = reviews.reduce((s, r) => s + r.rating, 0);
  const avg = sum / reviews.length;
  return {
    "@type": "AggregateRating",
    ratingValue: avg.toFixed(1),
    bestRating: 5,
    worstRating: 1,
    reviewCount: reviews.length,
    itemReviewed: { "@id": ORG_ID },
  };
};

// Core organisation node — referenced everywhere via @id
export const organizationSchema = (lang = "en") => ({
  "@context": "https://schema.org",
  "@type": ["Organization", "RealEstateAgent", "LocalBusiness"],
  "@id": ORG_ID,
  name: "IRE Berlin",
  legalName: "IRE Berlin GmbH",
  url: SITE_URL,
  logo: LOGO_URL,
  image: LOGO_URL,
  description:
    lang === "de"
      ? "Strategische Immobilien-Investmentberatung für Gutverdiener und Expats in Deutschland. Steueroptimierte Strukturen, vollständige Umsetzung, keine Maklergebühren."
      : "Strategic real estate investment advisory for high-income expats in Germany. Tax-optimized structures, full execution, zero brokerage fees.",
  email: "info@ireberlin.com",
  telephone: "+49 162 1871064",
  priceRange: "€€€€",
  areaServed: [
    { "@type": "Country", name: "Germany" },
    { "@type": "City", name: "Berlin" },
  ],
  address: {
    "@type": "PostalAddress",
    addressCountry: "DE",
    addressLocality: "Berlin",
    addressRegion: "Berlin",
  },
  sameAs: [],
  knowsLanguage: ["en", "de"],
  serviceType: [
    "Real Estate Investment Advisory",
    "Tax-Optimised Property Acquisition",
    "Buy-to-Let Strategy",
    "Property Management",
  ],
  aggregateRating: aggregateRatingNode(clientReviews),
  review: reviewNodes(clientReviews),
});

// WebSite schema — enables the SearchAction sitelinks searchbox in Google
export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: "IRE Berlin",
  publisher: { "@id": ORG_ID },
  inLanguage: ["en", "de"],
});

// Breadcrumb schema — give crawlers your hierarchy
export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

// FAQ schema — array of { question, answer } pairs
export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.answer,
    },
  })),
});

// Service schema (for Services page)
export const serviceSchema = (lang = "en") => ({
  "@context": "https://schema.org",
  "@type": "Service",
  provider: { "@id": ORG_ID },
  serviceType:
    lang === "de"
      ? "Strategische Immobilien-Investmentberatung"
      : "Strategic Real Estate Investment Advisory",
  areaServed: { "@type": "Country", name: "Germany" },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name:
      lang === "de"
        ? "Sieben strategische Säulen"
        : "Seven Strategic Pillars",
    itemListElement: [
      lang === "de" ? "Objektauswahl" : "Property Curation",
      lang === "de" ? "Steuerstrukturierung" : "Tax Structuring",
      lang === "de" ? "Finanzierung" : "Financing",
      lang === "de" ? "Mietstrategie" : "Rental Strategy",
      lang === "de" ? "Rechtliche Umsetzung" : "Legal Execution",
      lang === "de" ? "Verwaltung" : "Property Management",
      lang === "de" ? "Portfoliowachstum" : "Portfolio Growth",
    ].map((name, i) => ({
      "@type": "Offer",
      position: i + 1,
      itemOffered: { "@type": "Service", name },
    })),
  },
});

// Apartment / RealEstateListing schema for Off Market Karlshorst
export const projectSchema = (lang = "en") => ({
  "@context": "https://schema.org",
  "@type": "ApartmentComplex",
  name: "Off Market Karlshorst",
  description:
    lang === "de"
      ? "100 KfW 40 NH Wohnungen in Berlin-Karlshorst (Lichtenberg) — Übergabe Q4 2028. Energie-effizientes Neubau-Investment in Berlins zweitschnellst wachsendem Stadtteil."
      : "100 KfW 40 NH apartments in Berlin-Karlshorst (Lichtenberg) — handover Q4 2028. Energy-efficient new-build investment in Berlin's second-fastest-growing district.",
  numberOfAccommodationUnits: 100,
  address: {
    "@type": "PostalAddress",
    addressCountry: "DE",
    addressLocality: "Berlin",
    addressRegion: "Berlin-Karlshorst",
  },
  amenityFeature: [
    { "@type": "LocationFeatureSpecification", name: "KfW 40 NH energy standard" },
    { "@type": "LocationFeatureSpecification", name: "Walk to S-Bahn Karlshorst (5 min)" },
    { "@type": "LocationFeatureSpecification", name: "25 min to Berlin Mitte" },
  ],
  provider: { "@id": ORG_ID },
});
